import { orgSelectors } from 'ducks/orgs'
import { NotFoundVersioned } from 'elements/NotFoundVersioned'
import UiRoute from 'elements/UiRoute'
import { styled } from 'opensolar-ui'
import { HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import { WithPermissions } from 'react-admin'
import { useSelector } from 'react-redux'
import { Redirect, Switch, useLocation } from 'react-router-dom'
import type { ComponentTypes } from 'types/selectComponent'
import { useFeatureFlag } from 'util/featureFlags'
import AfterConnectPage from './AfterConnectPage'
import { DEFAULT_SHOP_CATEGORIES, useShopCategoryConfig } from './categoryConfig'
import CityPlumbingCheckout from './checkout/cityPlumbing/CityPlumbingCheckout'
import CityPlumbingCheckoutV2 from './checkout/cityPlumbing/CityPlumbingCheckoutV2'
import SolarJuiceCheckoutPage from './checkout/solarOutlet/solarJuice/SolarJuiceCheckoutPage'
import AllDealsPage from './deals/AllDealsPage'
import ProductDetailPage from './detail/ProductDetailPage'
import ShopPage from './home/OrderingHome'
import RecommendationsListPage from './home/RecommendationsListPage'
import OrderByProjectCartPage from './orderByProjectCart/OrderByProjectCartPage'
import OrderByProjectPage from './OrderByProjectPage'
import OrderConfirmationPage from './OrderConfirmationPage'
import ReviewCartPage from './reviewCart/ReviewCartPage'
import ShopResultsPage from './ShopResultsPage'

const RoutesWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 'min-content',
  width: '100%',
  minWidth: 0,
})

export const getSearchResultsPathname = (searchKey: string) => `/shop/search/${searchKey}`

export const PREFIXED_COMPONENT_TYPE_SEARCH_ROUTES: { searchKey: string; componentType: ComponentTypes }[] =
  DEFAULT_SHOP_CATEGORIES.map((category) => ({
    searchKey: category.searchKey,
    componentType: category.componentType,
  }))

export const useComponentTypeByRoute = () => {
  const location = useLocation()
  const { categories } = useShopCategoryConfig()

  const searchRoute = categories.find((cat) => getSearchResultsPathname(cat.searchKey) === location.pathname)
  return searchRoute?.componentType
}

const DynamicShopResultsPage = ({
  searchKey,
  distributor,
}: {
  searchKey: string
  distributor?: HardwareSupplierFilterKeyType
}) => {
  const { getCategoryBySearchKey, getSubtypesForCategory } = useShopCategoryConfig()
  const category = getCategoryBySearchKey(searchKey)
  const resultsPageKey = `${searchKey}:${distributor || 'all'}`

  if (category) {
    const subtypes = getSubtypesForCategory(category)
    return (
      <WithPermissions
        key={resultsPageKey}
        authParams={{ permission: 'is_pro' }}
        searchText={''}
        componentType={category.componentType}
        otherSubtypes={subtypes.length > 0 ? subtypes : undefined}
        categoryName={category.name}
        distributor={distributor}
        component={ShopResultsPage}
      />
    )
  }

  return (
    <WithPermissions
      key={resultsPageKey}
      authParams={{ permission: 'is_pro' }}
      searchText={searchKey}
      distributor={distributor}
      component={ShopResultsPage}
    />
  )
}

const OrderingRoutes = () => {
  const supplier = useSelector(orgSelectors.getSelectedHardwareSupplier)
  const enableMultiProjectOrdering = useFeatureFlag('shop_multi_project_ordering', 'on')

  return (
    <RoutesWrapper>
      <Switch>
        <UiRoute
          exact
          path={'/shop'}
          render={() => {
            return <WithPermissions resource={'shop_hardware_access'} component={ShopPage} />
          }}
        />
        <UiRoute
          exact
          path={'/shop/deals'}
          render={() => <WithPermissions authParams={{ permission: 'is_pro' }} component={AllDealsPage} />}
        />
        {/* Redirect legacy "/distributor" routes used in marketing and SEO to new "/supplier" route */}
        <UiRoute
          exact
          path={'/shop/distributor/:distributor'}
          render={({ match }) => {
            const distributor = match.params.distributor
            return <Redirect to={`/shop/supplier/${distributor}`} />
          }}
        />
        <UiRoute
          exact
          path={'/shop/supplier/:distributor'}
          render={({ match }) => {
            const distributor = match.params.distributor || undefined
            return (
              <WithPermissions
                authParams={{ permission: 'is_pro' }}
                distributor={distributor as HardwareSupplierFilterKeyType}
                component={ShopPage}
              />
            )
          }}
        />
        <UiRoute
          exact
          path={'/shop/search/:searchKey/supplier/:distributor'}
          render={({ match }) => {
            const distributor = match.params.distributor
            const searchKey = match.params.searchKey || ''
            return (
              <DynamicShopResultsPage
                searchKey={searchKey}
                distributor={distributor as HardwareSupplierFilterKeyType}
              />
            )
          }}
        />
        <UiRoute
          exact
          path={'/shop/recommendations/:source(designs|purchases)'}
          render={({ match }) => {
            const source = match.params.source as 'designs' | 'purchases'
            return (
              <WithPermissions
                authParams={{ permission: 'is_pro' }}
                source={source}
                component={RecommendationsListPage}
              />
            )
          }}
        />
        <UiRoute
          path={'/shop/product/:code'}
          render={({ match, location }) => {
            const componentCode = match.params.code || ''
            return (
              <WithPermissions
                authParams={{ permission: 'is_pro' }}
                componentCode={componentCode}
                component={ProductDetailPage}
              />
            )
          }}
        />
        <UiRoute
          exact
          path="/shop/search"
          render={({ location }) => {
            const state = location.state as any
            return (
              <WithPermissions
                authParams={{ permission: 'is_pro' }}
                searchText={''}
                initialComponentsData={state?.componentsData}
                initialTitle={state?.title}
                component={ShopResultsPage}
              />
            )
          }}
        />
        {/* /shop/search/:searchText must come after the exact /shop/search route above */}
        <UiRoute
          path="/shop/search/:searchKey"
          render={({ match }) => {
            const searchKey = match.params.searchKey || ''
            return <DynamicShopResultsPage searchKey={searchKey} />
          }}
        />
        <UiRoute exact path={'/shop/order_confirmation/:orderId'} component={OrderConfirmationPage} />
        <UiRoute exact path="/shop/after_connect/:distributor" component={AfterConnectPage} />
        <UiRoute exact path="/shop/after_connect/:distributor/:page" component={AfterConnectPage} />
        {enableMultiProjectOrdering && (
          <UiRoute
            exact
            path="/multi_project_ordering"
            render={() => <WithPermissions resource={'shop_hardware_access'} component={OrderByProjectPage} />}
          />
        )}

        {enableMultiProjectOrdering && (
          <UiRoute
            exact
            path="/multi_project_ordering/cart"
            render={() => <WithPermissions resource={'shop_hardware_access'} component={OrderByProjectCartPage} />}
          />
        )}
        <UiRoute exact path={'/shop/cart'} component={ReviewCartPage} />
        {supplier === 'outlet' && <UiRoute exact path={'/shop/cart/outlet'} component={SolarJuiceCheckoutPage} />}
        {supplier === 'city_plumbing' && (
          <>
            <UiRoute exact path={'/shop/cart/city_plumbing'} component={CityPlumbingCheckoutV2} />
            <UiRoute exact path={'/shop/cart/city_plumbing/:jobListId'} component={CityPlumbingCheckout} />
          </>
        )}
        <UiRoute path="/" component={NotFoundVersioned} />
      </Switch>
    </RoutesWrapper>
  )
}

export default OrderingRoutes
